// Firebase Authentication for the demo gate. Frontend-only: no backend calls.
// Swap this file's internals for real API calls later without touching callers.
import {
  createUserWithEmailAndPassword,
  deleteUser,
  fetchSignInMethodsForEmail,
  onAuthStateChanged,
  signInWithEmailAndPassword,
  signOut,
  type User,
} from "firebase/auth";
import { doc, getDoc, serverTimestamp, setDoc, Timestamp } from "firebase/firestore";
import { auth, db } from "@/integrations/firebase/client";
import type { UserRole } from "@/config/demoAuth.config";
import type { DemoUserProfile } from "@/interfaces/demoUser.interface";
import { validateAccessCode } from "@/services/accessCode.service";
import { registerAccessCode } from "@/lib/posthog";

const USERS_COLLECTION = "users";

export class AuthFlowError extends Error {}

// Firebase's default "email enumeration protection" collapses user-not-found
// and wrong-password into auth/invalid-credential, so that combined case gets
// one message; the two specific codes are still handled for older projects.
const FRIENDLY_ERRORS: Record<string, string> = {
  "auth/invalid-credential": "Incorrect email or password. Please try again.",
  "auth/user-not-found": "No account found with that email address.",
  "auth/wrong-password": "Incorrect password. Please try again.",
  "auth/invalid-email": "That doesn't look like a valid email address.",
  "auth/email-already-in-use": "An account with this email already exists.",
  "auth/weak-password": "Password must be at least 6 characters.",
  "auth/too-many-requests": "Too many attempts. Please wait a moment and try again.",
  "auth/network-request-failed": "Network error — check your connection and try again.",
};

function friendlyMessage(error: unknown, fallback: string): string {
  const code = (error as { code?: string } | undefined)?.code;
  return (code && FRIENDLY_ERRORS[code]) || fallback;
}

export function subscribeToAuthChanges(callback: (user: User | null) => void): () => void {
  return onAuthStateChanged(auth, callback);
}

export async function signIn(email: string, password: string): Promise<User> {
  try {
    const credential = await signInWithEmailAndPassword(auth, email, password);
    return credential.user;
  } catch (error) {
    throw new AuthFlowError(friendlyMessage(error, "Unable to sign in. Please try again."));
  }
}

export async function isEmailRegistered(email: string): Promise<boolean> {
  const methods = await fetchSignInMethodsForEmail(auth, email);
  return methods.length > 0;
}

interface SignUpParams {
  email: string;
  password: string;
  role: UserRole;
  organization: string;
  accessCode: string;
}

const ACCESS_CODE_ERRORS: Record<NonNullable<Awaited<ReturnType<typeof validateAccessCode>>["reason"]>, string> = {
  empty: "Please enter an access code.",
  not_found: "Invalid access code. Please check the code and try again.",
  inactive: "This access code is no longer active.",
  exhausted: "This access code has reached its usage limit.",
  error: "Unable to verify the access code right now. Please try again.",
};

export async function signUp({ email, password, role, organization, accessCode }: SignUpParams): Promise<User> {
  const codeCheck = await validateAccessCode(accessCode);
  if (!codeCheck.ok) {
    throw new AuthFlowError(ACCESS_CODE_ERRORS[codeCheck.reason ?? "error"]);
  }
  registerAccessCode();

  try {
    if (await isEmailRegistered(email)) {
      throw new AuthFlowError("An account with this email already exists.");
    }
  } catch (error) {
    if (error instanceof AuthFlowError) throw error;
    // Best-effort pre-check only — creation below still enforces uniqueness.
  }

  let user: User;
  try {
    const credential = await createUserWithEmailAndPassword(auth, email, password);
    user = credential.user;
  } catch (error) {
    throw new AuthFlowError(friendlyMessage(error, "Unable to create account. Please try again."));
  }

  try {
    await setDoc(doc(db, USERS_COLLECTION, user.uid), {
      email,
      role,
      organization,
      createdAt: serverTimestamp(),
    });
  } catch (error) {
    // Roll back the orphaned Auth account so the email isn't permanently
    // stuck in "already in use" with no visible Firestore profile.
    await deleteUser(user).catch(() => {});
    throw new AuthFlowError(
      friendlyMessage(error, "Unable to complete registration. Please try again."),
    );
  }

  return user;
}

export async function getUserProfile(uid: string): Promise<DemoUserProfile | null> {
  const snap = await getDoc(doc(db, USERS_COLLECTION, uid));
  if (!snap.exists()) return null;
  const data = snap.data() as { email?: string; role?: UserRole; organization?: string; createdAt?: Timestamp };
  return {
    uid,
    email: data.email ?? "",
    role: data.role ?? "other",
    organization: data.organization ?? "",
    createdAt: data.createdAt?.toMillis() ?? Date.now(),
  };
}

export async function signOutUser(): Promise<void> {
  await signOut(auth);
}
